import React from 'react'
import {
  BookOpen,
  CheckCircle,
  Clock,
  AlertTriangle,
  Award,
  TrendingUp,
  DollarSign,
  Users,
  Loader2
} from 'lucide-react'
import { Course, getCoursesByStatus } from '@/lib/real-data-service'

interface CourseStatsProps {
  courses: Course[]
  userRole: string
  loading?: boolean
}

export function CourseStats({ courses, userRole, loading = false }: CourseStatsProps) {
  const completedCourses = getCoursesByStatus(courses, 'available')
  const inProgressCourses = getCoursesByStatus(courses, 'draft')
  const totalHours = courses.reduce((sum, course) => sum + course.duration, 0)
  const completedHours = completedCourses.reduce((sum, course) => sum + course.duration, 0)

  // Calculate total investment (simplified)
  const totalInvestment = 0 // Would calculate from real cost data

  // Employee stats
  const employeeStats = [
    {
      label: 'Cursos Concluídos',
      value: completedCourses.length.toString(),
      icon: CheckCircle,
      color: 'text-success'
    },
    {
      label: 'Em Andamento',
      value: inProgressCourses.length.toString(),
      icon: Clock,
      color: 'text-warning'
    },
    {
      label: 'Horas Concluídas',
      value: `${completedHours}h`,
      icon: TrendingUp,
      color: 'text-brand-primary'
    },
    {
      label: 'Certificados',
      value: completedCourses.filter(c => c.completionCertificate).length.toString(),
      icon: Award,
      color: 'text-info'
    }
  ]

  // Manager stats
  const managerStats = [
    {
      label: 'Total de Cursos',
      value: courses.length.toString(),
      icon: BookOpen,
      color: 'text-brand-primary'
    },
    {
      label: 'Cursos da Equipe',
      value: '0',
      icon: Users,
      color: 'text-info'
    },
    {
      label: 'Conclusão Média',
      value: '0%',
      icon: TrendingUp,
      color: 'text-success'
    },
    {
      label: 'Atrasados',
      value: '0',
      icon: AlertTriangle,
      color: 'text-brand-gray'  // Simplified for now
    }
  ]

  // Additional stats for employees with investment tracking
  const employeeStatsWithInvestment = [
    ...employeeStats,
    {
      label: 'Investimento',
      value: totalInvestment > 0 ? `$${Math.round(totalInvestment)}` : '$0',
      icon: DollarSign,
      color: 'text-warning'
    },
    {
      label: 'Atrasados',
      value: '0',
      icon: AlertTriangle,
      color: 'text-brand-gray'  // Simplified for now
    }
  ]

  const statsToShow = userRole === 'employee'
    ? employeeStatsWithInvestment.slice(0, 4) // Show first 4 for clean layout
    : managerStats

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Loader2 className="animate-spin text-brand-gray" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-brand-gray mb-2">
              --
            </div>
            <div className="text-xs text-brand-gray uppercase tracking-wider">
              Carregando...
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
      {statsToShow.map((stat, index) => {
        const Icon = stat.icon
        return (
          <div key={index} className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Icon className={stat.color} size={20} />
            </div>
            <div className={`text-xl sm:text-2xl lg:text-3xl font-display font-bold ${stat.color} mb-2`}>
              {stat.value}
            </div>
            <div className="text-xs text-brand-gray uppercase tracking-wider">
              {stat.label}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface ProgressOverviewProps {
  courses: Course[]
  loading?: boolean
}

export function ProgressOverview({ courses, loading = false }: ProgressOverviewProps) {
  const completedCourses = getCoursesByStatus(courses, 'completed')
  const inProgressCourses = getCoursesByStatus(courses, 'in-progress')
  const totalHours = courses.reduce((sum, course) => sum + course.duration, 0)
  const completedHours = completedCourses.reduce((sum, course) => sum + course.duration, 0)
  
  const progressPercentage = totalHours > 0 ? (completedHours / totalHours) * 100 : 0

  // Calculate in-progress hours
  const inProgressHours = inProgressCourses.reduce((total, course) => {
    const courseProgress = ((course.progress || 0) / 100) * course.duration
    return total + courseProgress
  }, 0)

  const totalProgressHours = completedHours + inProgressHours
  const overallProgress = totalHours > 0 ? (totalProgressHours / totalHours) * 100 : 0

  if (loading) {
    return (
      <div className="card mb-6">
        <h3 className="font-display text-lg font-semibold text-white mb-4">
          Visão Geral do Progresso
        </h3>

        <div className="space-y-4">
          {/* Overall Progress Loading */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-white font-medium">Progresso Geral</span>
              <span className="text-brand-primary font-bold">--</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill bg-brand-gray animate-pulse" style={{ width: '30%' }} />
            </div>
            <div className="flex justify-between text-xs text-brand-gray mt-1">
              <span>-- h concluídas</span>
              <span>-- h totais</span>
            </div>
          </div>

          {/* Progress Stats Loading */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-brand-gray/20">
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-gray flex items-center justify-center">
                <Loader2 className="animate-spin" size={20} />
              </div>
              <div className="text-xs text-brand-gray">Concluídos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-gray flex items-center justify-center">
                <Loader2 className="animate-spin" size={20} />
              </div>
              <div className="text-xs text-brand-gray">Em Progresso</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-gray flex items-center justify-center">
                <Loader2 className="animate-spin" size={20} />
              </div>
              <div className="text-xs text-brand-gray">Horas Estudadas</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card mb-6">
      <h3 className="font-display text-lg font-semibold text-white mb-4">
        Visão Geral do Progresso
      </h3>
      
      <div className="space-y-4">
        {/* Overall Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-medium">Progresso Geral</span>
            <span className="text-brand-primary font-bold">{Math.round(overallProgress)}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-brand-gray mt-1">
            <span>{Math.round(totalProgressHours)}h concluídas</span>
            <span>{totalHours}h totais</span>
          </div>
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-brand-gray/20">
          <div className="text-center">
            <div className="text-2xl font-bold text-success">{completedCourses.length}</div>
            <div className="text-xs text-brand-gray">Concluídos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">{inProgressCourses.length}</div>
            <div className="text-xs text-brand-gray">Em Progresso</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-brand-primary">{Math.round(completedHours)}h</div>
            <div className="text-xs text-brand-gray">Horas Estudadas</div>
          </div>
        </div>
      </div>
    </div>
  )
}
